import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";
import { truncateToTwoDecimals } from "../../../../../../utility/helper";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { roi, date } = payload[0].payload;

    const roiPercentage = truncateToTwoDecimals(roi);
    return (
      <div
        style={{
          padding: "10px",
          color: "#333",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        }}
      >
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>ROI:</strong>
          <p style={{ color: "#333" }}>{roiPercentage}%</p>
        </div>
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>Date:</strong>
          <p style={{ color: "#333" }}>{date}</p>
        </div>
      </div>
    );
  }
  return null;
};

const RechartExample = ({ graphROI, companyDetails }) => {
  const transformedData = graphROI.chart_data?.map((item) => ({
    date: item.date,
    value: item.value,
    roi: item.roi,
  }));

  const formatXAxis = (tickItem) => {
    return moment(tickItem).format("DD MMM");
  };

  return (
    <div className="flex gap-4">
      <div className="border p-5 border-gray-600 w-[60%]">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={transformedData}>
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#00BFFF" />
                <stop offset="100%" stopColor="#00BFFF" />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="0 1" stroke="#ccc" />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              axisLine={false}
              tick={{ fill: "white", fontSize: 12 }}
              tickLine={false}
              angle={-30}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              minTickGap={15}
              tick={{ fill: "white", fontSize: 12 }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <Line
              type="linear"
              dataKey="roi"
              stroke="url(#colorGradient)"
              dot={false}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="relative overflow-x-auto w-[40%] border border-gray-600">
        <h1 className="text-2xl text-white font-semibold text-center py-2">
          Company Overview
        </h1>
        <div className=" p-5 text-gray-400">
          <div className="mb-5 flex">
            <span className="font-medium text-white w-40">Company Name</span>
            <span>{companyDetails?.company_name || "N/A"}</span>
          </div>
          <div className="mb-5 flex">
            <span className="font-medium text-white w-40">CEO</span>
            <span>{companyDetails?.ceo || "N/A"}</span>
          </div>
          <div className="mb-5 flex items-center">
            <span className="font-medium text-white w-40">Company Logo</span>
            {companyDetails?.image ? (
              <img
                src={companyDetails.image}
                alt="Company Logo"
                className="h-6 w-6"
              />
            ) : (
              <span>N/A</span>
            )}
          </div>
          <div className="mb-5 flex">
            <span className="font-medium text-white w-40">Sector</span>
            <span>{companyDetails?.sector || "N/A"}</span>
          </div>
          <div className="mb-5 flex">
            <span className="font-medium text-white w-40">Industry</span>
            <span>{companyDetails?.industry || "N/A"}</span>
          </div>
          <div className="flex mb-5">
            <span className="font-medium text-white w-40">Exchange</span>
            <span>{companyDetails?.exchange || "N/A"}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-white w-40">Description</span>
            <span className="group relative">
              <span className="block max-w-xs overflow-hidden text-ellipsis whitespace-nowrap group-hover:whitespace-normal group-hover:overflow-visible">
                {companyDetails?.description
                  ? companyDetails.description
                      .split(" ")
                      .slice(0, 40)
                      .join(" ") + "..."
                  : "N/A"}
              </span>
              {companyDetails?.description?.split(" ").length > 40 && (
                <span className="text-blue-400 underline cursor-pointer group-hover:hidden">
                  View More
                </span>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RechartExample;
